import { http, xhrUpload } from './client';

export * from './client';

// ---- Auth ----
export type AuthUser = {
  id: number; email: string;
  first_name?: string; last_name?: string; display_name?: string;
  is_platform_admin: boolean;
};

// Catalyst ROWIDs are BIGINTs serialised by the backend as strings (they
// overflow JS Number). Always treat tenant_id as `string | number` on the
// wire and avoid Number() coercion before it reaches zcql.
export type TenantId = string | number;

export type SignupResponse = {
  token: string; expires_at: string;
  user: AuthUser;
  tenant: { id: TenantId; name: string; tenant_type: 'solo' | 'chambers' };
};
export type LoginResponse = {
  token: string; expires_at: string;
  user: AuthUser;
  tenant_id: TenantId | null;
  memberships: { tenant_id: TenantId }[];
};

export const authApi = {
  signup: (body: { email: string; first_name: string; last_name: string; password: string; chambers_name: string; tenant_type?: 'solo' | 'chambers' }) =>
    http.post<SignupResponse>('/auth/signup', body),
  login: (body: { email: string; password: string }) =>
    http.post<LoginResponse>('/auth/login', body),
  me: () => http.get<{ user: AuthUser; memberships: Membership[]; session_tenant_id: TenantId | null }>('/auth/me'),
  switchTenant: (tenant_id: TenantId) => http.post('/auth/switch-tenant', { tenant_id: String(tenant_id) }),
  logout: () => http.post('/auth/logout'),
};

export type Membership = {
  tenant_id: TenantId; role: string; is_owner: boolean; is_super_admin: boolean;
  display_name: string; email: string;
  tenant_name: string; tenant_type: 'solo' | 'chambers';
  tenant_status: string; onboarding_complete: boolean; trial_ends_at?: string;
};

// ---- Tenant ----
export const tenantApi = {
  get:    ()        => http.get<{ tenant: Tenant }>('/tenant'),
  update: (patch: Partial<Tenant>) => http.patch('/tenant', patch),
  onboarding: (step: string) => http.post(`/tenant/onboarding/${step}`),
};
export type Tenant = {
  ROWID: TenantId; name: string; legal_name?: string; tenant_type: 'solo'|'chambers';
  status: string; trial_ends_at?: string; onboarding_step: string; onboarding_complete: boolean;
  logo_object_id?: string; branding_json?: string | null; address_json?: string | null;
  vat_number?: string; tax_number?: string; default_currency: string; default_timezone: string;
};

// ---- Members ----
export const memberApi = {
  list:   () => http.get<{ members: Member[] }>('/members'),
  invite: (body: { email: string; role: string }) => http.post<{ invite_url: string }>('/members/invite', body),
  update: (id: number, patch: Partial<Member>) => http.patch(`/members/${id}`, patch),
  remove: (id: number) => http.del(`/members/${id}`),
  getSelf: () => http.get<{ member: Member }>('/members/self'),
  updateSelf: (patch: Partial<Member>) => http.patch('/members/self', patch),
  makeSuperAdmin:   (id: number) => http.post(`/members/${id}/make-super-admin`),
  revokeSuperAdmin: (id: number) => http.post(`/members/${id}/revoke-super-admin`),
  transferOwnership:(id: number) => http.post(`/members/${id}/transfer-ownership`, { confirm: 'TRANSFER' }),
};
export type Member = {
  ROWID: number; user_id: number; role: string; is_owner: boolean; is_super_admin: boolean;
  is_billing_contact: boolean; display_name: string; email: string; title?: string;
  advocate_class?: 'referral' | 'trust_account';
  ffc_number?: string; ffc_expires_at?: string; seniority?: string;
  pmt_completed_at?: string; cpd_points_current?: number; bar_membership?: string;
  default_vat_registered?: boolean | string;
  access_scope: 'all'|'restricted'; status: string;
};

// ---- Catalog ----
export const catalogApi = {
  jurisdictions:      () => http.get<{ jurisdictions: Jurisdiction[] }>('/catalog/jurisdictions'),
  courts:             (jurisdiction = 'ZA') => http.get<{ courts: Court[] }>(`/catalog/courts?jurisdiction=${jurisdiction}`),
  matterTypes:        (jurisdiction = 'ZA') => http.get<{ matter_types: MatterType[] }>(`/catalog/matter-types?jurisdiction=${jurisdiction}`),
  partyRoleTemplates: (matterType: string, jurisdiction = 'ZA') =>
    http.get<{ matter_type_id: number; roles: RoleTemplate[] }>(`/catalog/party-role-templates?matter_type=${matterType}&jurisdiction=${jurisdiction}`),
  proceduralStages:   (matterType: string, jurisdiction = 'ZA') =>
    http.get<{ stages: ProceduralStage[] }>(`/catalog/procedural-stages?matter_type=${matterType}&jurisdiction=${jurisdiction}`),
  documentTypes:      (jurisdiction = 'ZA') => http.get<{ document_types: DocumentType[] }>(`/catalog/document-types?jurisdiction=${jurisdiction}`),
  taxRates:           (jurisdiction = 'ZA') => http.get<{ tax_rates: TaxRate[] }>(`/catalog/tax-rates?jurisdiction=${jurisdiction}`),
  interlocutoryTypes: (parentMatterTypeCode?: string, jurisdiction = 'ZA') => {
    const qs = new URLSearchParams({ jurisdiction });
    if (parentMatterTypeCode) qs.set('parent_matter_type_code', parentMatterTypeCode);
    return http.get<{ interlocutory_types: InterlocutoryType[]; parent_matter_type_code: string | null }>(`/catalog/interlocutory-types?${qs}`);
  },

  // Tenant-owned forums (tribunals, arbitration panels, private fora).
  listTenantCourts:   (jurisdiction = 'ZA') => http.get<{ courts: TenantCourt[] }>(`/catalog/tenant-courts?jurisdiction=${jurisdiction}`),
  createTenantCourt:  (body: NewTenantCourt) => http.post<{ id: number }>('/catalog/tenant-courts', body),
  updateTenantCourt:  (id: number, patch: Partial<TenantCourt>) => http.patch(`/catalog/tenant-courts/${id}`, patch),
  deleteTenantCourt:  (id: number) => http.del(`/catalog/tenant-courts/${id}`),

  courtCalendar: (jurisdiction = 'ZA', from?: string, to?: string) => {
    const qs = new URLSearchParams({ jurisdiction });
    if (from) qs.set('from', from);
    if (to)   qs.set('to',   to);
    return http.get<CourtCalendar>(`/catalog/court-calendar?${qs}`);
  },
};

export type CourtCalendarEntry = {
  ROWID: number;
  court_date: string;
  kind: 'holiday' | 'recess' | 'court_day';
  note?: string;
};
export type CourtCalendar = {
  jurisdiction: string;
  from: string;
  to: string;
  entries: CourtCalendarEntry[];
};

// ---- Util (deadline calculator) ----
export const utilApi = {
  computeDeadline: (body: { from: string; offset_days: number; mode?: 'court'|'calendar' }) =>
    http.post<{ due_date: string; weekday: string; is_court_day: boolean; mode: string; offset_days: number; from: string }>('/util/deadline', body),
  isCourtDay: (date: string) => http.get<{ date: string; is_court_day: boolean }>(`/util/is-court-day?date=${date}`),
};
export type Jurisdiction = { code: string; name: string; vat_default_rate?: number; currency?: string; active: boolean };
export type Court        = {
  ROWID: number; code: string; name: string;
  /** constitutional|sca|high|specialised|tribunal|magistrate_court|small_claims|arbitration|other.
   *  `regional_magistrate` and `district_magistrate` are deprecated — collapsed into magistrate_court. */
  tier: string;
  division?: string; seat?: string;
  /** Magistrates' courts only: structural fields from the DoJ register. */
  magisterial_district?: string | null;
  sub_district?: string | null;
  is_main_seat?: boolean | string;
  /** magistrate|branch|detached|periodical (lower courts only) */
  court_subtype?: string | null;
  is_equality_court?: boolean | string;
  is_small_claims_court?: boolean | string;
  court_online_enabled: boolean; caselines_enabled: boolean;
  source?: 'system'|'tenant';
};
export type TenantCourt  = {
  ROWID: number; jurisdiction_id: string; code: string; name: string;
  tier: string; division?: string; seat?: string;
  court_online_enabled: boolean | string; caselines_enabled: boolean | string;
  display_order: number; active: boolean | string; notes?: string; created_by?: number;
};
export type NewTenantCourt = {
  code: string; name: string; tier: string;
  division?: string; seat?: string; notes?: string;
  court_online_enabled?: boolean; caselines_enabled?: boolean; display_order?: number;
  jurisdiction_id?: string;
};
export type MatterType   = { ROWID: number; code: string; name: string; description?: string; category?: string; auto_citation: boolean };
export type RoleTemplate = { role_code: string; role_label_singular: string; role_label_plural: string; side: 'initiating'|'responding'|'other'|'neutral'; min_count: number; max_count?: number; is_numbered: boolean; citation_side: 'left'|'right'|'omit'; display_order: number; is_auto_added: boolean; auto_party_hint?: string; };
export type ProceduralStage = { ROWID: number; code: string; name: string; sequence: number; is_terminal: boolean };
export type DocumentType = { ROWID: number; code: string; name: string; category?: string };
export type InterlocutoryType = {
  ROWID: number;
  code: string;
  name: string;
  category: 'pleadings_stage' | 'pre_trial' | 'default_judgment' | 'proceedings_management' | 'application_specific' | 'post_judgment';
  description?: string;
  rule_citation?: string;
  source?: string;
  trigger_text?: string;
  compatible_with_action: boolean | string;
  compatible_with_application: boolean | string;
  suspends_parent_deadlines: boolean | string;
  appealable_on_grant?: 'yes' | 'no' | 'with_leave' | 'depends';
  appealable_on_refusal?: 'yes' | 'no' | 'with_leave' | 'depends';
  display_order: number;
};
export type TaxRate      = { code: string; name: string; rate_percent: number; effective_from: string; is_default: boolean };

// ---- Attorneys (firms + individuals) ----
export const attorneyApi = {
  listFirms:   (q?: string) => http.get<{ firms: AttorneyFirm[] }>(`/attorney-firms${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  getFirm:     (id: number) => http.get<{ firm: AttorneyFirm; attorneys: Attorney[] }>(`/attorney-firms/${id}`),
  createFirm:  (body: Partial<AttorneyFirm>) => http.post<{ id: number }>('/attorney-firms', body),
  updateFirm:  (id: number, patch: Partial<AttorneyFirm>) => http.patch(`/attorney-firms/${id}`, patch),
  deleteFirm:  (id: number) => http.del(`/attorney-firms/${id}`),

  list:    (params: { q?: string; firm_id?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.q)       qs.set('q', params.q);
    if (params.firm_id) qs.set('firm_id', String(params.firm_id));
    const str = qs.toString();
    return http.get<{ attorneys: Attorney[] }>(`/attorneys${str ? `?${str}` : ''}`);
  },
  create:  (body: Partial<Attorney>) => http.post<{ id: number }>('/attorneys', body),
  update:  (id: number, patch: Partial<Attorney>) => http.patch(`/attorneys/${id}`, patch),
  remove:  (id: number) => http.del(`/attorneys/${id}`),
};
export type AttorneyFirm = {
  ROWID: number; name: string; vat_number?: string;
  email?: string; phone?: string; address_json?: string | null; notes?: string;
};
export type Attorney = {
  ROWID: number; firm_id?: number | null;
  title?: string; first_name?: string; last_name: string;
  email?: string; phone?: string; notes?: string;
};

// ---- Parties ----
export const partyApi = {
  list:   (q?: string) => http.get<{ parties: Party[] }>(`/parties${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  create: (body: Partial<Party>) => http.post<{ id: number }>('/parties', body),
  update: (id: number, patch: Partial<Party>) => http.patch(`/parties/${id}`, patch),
  remove: (id: number) => http.del(`/parties/${id}`),
};
export type Party = {
  ROWID: number;
  entity_type: 'natural_person'|'juristic_person'|'state_organ'|'trust'|'partnership'|'sole_proprietor'|'deceased_estate'|'minor_assisted'|'other';
  display_name: string; full_legal_name?: string;
  id_or_registration_no?: string; vat_number?: string; tax_number?: string;
  email?: string; phone?: string; address_json?: string; notes?: string;
};

// ---- Matters ----
export const matterApi = {
  list:   (params: { status?: string; q?: string } = {}) => {
    const qs = new URLSearchParams(params as Record<string,string>).toString();
    return http.get<{ matters: MatterSummary[] }>(`/matters${qs ? `?${qs}` : ''}`);
  },
  // IDs are widened to string|number: Catalyst ROWIDs can exceed JS
  // safe-integer precision, so callers that hold the raw URL param should
  // keep the string form end-to-end.
  create: (body: NewMatterInput) => http.post<{ id: number | string }>('/matters', body),
  get:    (id: number | string) => http.get<{ matter: MatterDetail; parties: MatterParty[] }>(`/matters/${id}`),
  update: (id: number | string, patch: Partial<MatterDetail>) => http.patch(`/matters/${id}`, patch),
  advanceStage: (id: number | string, target_stage_id?: number) => http.post(`/matters/${id}/advance-stage`, target_stage_id ? { target_stage_id } : undefined),
  /** Skip an optional stage. Server enforces is_optional=true. */
  skipStage: (id: number | string, stage_id: number | string, reason?: string) =>
    http.post(`/matters/${id}/skip-stage`, { stage_id, reason }),
  finalise: (id: number | string) => http.post(`/matters/${id}/finalise`),
  reopen:   (id: number | string) => http.post(`/matters/${id}/reopen`),
  archive:  (id: number | string) => http.post(`/matters/${id}/archive`),
  remove:   (id: number | string) => http.del(`/matters/${id}`),
  setConfidentiality: (id: number | string, mode: 'standard'|'restricted'|'walled', reason?: string) =>
    http.put(`/matters/${id}/confidentiality`, { mode, reason }),
  events: (id: number | string) => http.get<{ events: MatterEvent[] }>(`/matters/${id}/events`),

  // Access control
  getAccess: (id: number | string) => http.get<{ access: MatterAccessGrant[] }>(`/matters/${id}/access`),
  setAccess: (id: number | string, entries: { user_id: number; access_level: MatterAccessLevel }[]) =>
    http.put(`/matters/${id}/access`, { entries }),

  // Stage playbook — future procedural roadmap
  stagePlaybook: (id: number | string) => http.get<{
    stages: StagePlaybookEntry[];
    current_stage_id: string | null;
  }>(`/matters/${id}/stage-playbook`),

  // Interlocutories (child matters per master §10.2). Most do NOT suspend
  // parent deadlines; the child carries `interlocutory_type` and inherits
  // parent parties as defaults.
  listInterlocutories: (id: number | string) =>
    http.get<{ interlocutories: InterlocutoryChild[] }>(`/matters/${id}/interlocutories`),
  createInterlocutory: (id: number | string, body: NewInterlocutoryInput) =>
    http.post<{ id: string; interlocutory_type: string; parent_matter_id: string; suspends_parent_deadlines: boolean }>(`/matters/${id}/interlocutories`, body),

  // Rule 41A mediation (master §1.2). Refer pauses pleadings deadlines;
  // resume marks the joint minute filed and re-fires reminders.
  referMediation: (id: number | string) => http.post<{ ok: true; mediation_referred_at: string; suspended_count: number }>(`/matters/${id}/mediation/refer`),
  resumeMediation: (id: number | string) => http.post<{ ok: true; mediation_joint_minute_at: string; resumed_count: number }>(`/matters/${id}/mediation/resume`),

  // Parties on matter
  listParties: (id: number | string) => http.get<{ parties: MatterParty[] }>(`/matters/${id}/parties`),
  addParty: (id: number | string, body: { party_id: number; role_code: string; role_order?: number; is_my_client?: boolean }) =>
    http.post<{ id: number }>(`/matters/${id}/parties`, body),
  updateParty: (id: number | string, pid: number, patch: Partial<MatterParty>) => http.patch(`/matters/${id}/parties/${pid}`, patch),
  withdrawParty: (id: number | string, pid: number) => http.post(`/matters/${id}/parties/${pid}/withdraw`),
  removeParty: (id: number | string, pid: number) => http.del(`/matters/${id}/parties/${pid}`),
};

export type MatterSummary = {
  ROWID: number; reference: string; matter_name: string; matter_name_override?: string;
  matter_type_id: number;
  court_id?: number; case_number?: string; status: string;
  current_stage_id?: number; primary_counsel_user_id?: number; opened_at: string;
};
/** Single source of truth for "what name should I display?" — prefers the user
 *  override over the auto-generated citation. Used everywhere matter names render. */
export const matterDisplayName = (m: { matter_name?: string; matter_name_override?: string | null }) =>
  m.matter_name_override || m.matter_name || '';
export type MatterDetail = MatterSummary & {
  jurisdiction_id: string;
  current_stage?: { ROWID: number; code: string; name: string };
  estimated_value_zar?: number; cost_scale?: string; notes?: string;
  court_online_case_id?: string; caselines_bundle_url?: string;
  confidentiality: { mode: string; reason?: string };
  matter_name_override?: string;
  nature_description?: string | null;
  court_freeform?: string | null;
  /** 'district' | 'regional' | null — Magistrates' Court ambit. */
  mc_jurisdiction?: 'district' | 'regional' | null;
  pro_bono_status?: 'none' | 'pro_bono' | 'pro_amico' | null;
};
export type MatterParty = {
  matter_party_id: number; party_id: number; display_name: string; entity_type: string;
  full_legal_name?: string; email?: string; phone?: string;
  role_code: string; role_label_snapshot: string; role_label_plural?: string; role_order: number;
  role_display_order?: number;
  side: string; is_my_client: boolean;
  represented_by_attorney_id?: number; represented_by_counsel_user_id?: number;
  joined_at?: string; left_at?: string; citation_include: boolean;
  citation_side?: 'left'|'right'|'omit'; is_numbered?: boolean;
};
export type NewMatterInput = {
  reference: string;
  // IDs accept string OR number — Catalyst ROWIDs are BIGINTs that don't
  // fit in JS Number, so callers should pass digit strings end-to-end.
  matter_type_id: number | string; court_id?: number | string; case_number?: string;
  /** Free-text fallback when no recognised court applies (tribunals, ad-hoc forums). */
  court_freeform?: string;
  instructing_attorney_id?: number | string; instructing_firm_id?: number | string;
  primary_counsel_user_id?: number | string; secondary_counsel_user_id?: number | string;
  opened_at?: string; estimated_value_zar?: number; cost_scale?: string; notes?: string;
  /** Free-text "what is this matter about" (separate from matter_type). */
  nature_description?: string;
  /** Mid-flow start: stage the advocate is currently in (else default to sequence 0). */
  starting_stage_id?: number | string;
  /** When starting mid-flow, also seed the standard task list for that stage? Default: false. */
  seed_starting_stage_tasks?: boolean;
  /** Magistrates' Court ambit: 'district' | 'regional'. Only meaningful when court tier === 'magistrate_court'. */
  mc_jurisdiction?: 'district' | 'regional';
};
export type MatterEvent = {
  ROWID: number; event_type: string; summary: string; actor_user_id?: number; occurred_at: string;
};
export type StagePlaybookEntry = {
  id: string; code: string; name: string; description?: string;
  sequence: number; is_terminal: boolean;
  /** Optional stages can be skipped via /matters/:id/skip-stage. */
  is_optional?: boolean;
  /** Pipe-separated reminder strings (NOT tasks). Render above the task list. */
  notes_csv?: string | null;
  default_duration_days?: number;
  /**
   * past:           advocate-owned task in this stage has been completed (✓).
   * past_unmarked:  matter has moved past this sequence but no advocate work was logged here
   *                 (e.g. the user fast-forwarded at create or advanced without completing tasks).
   *                 Renders with the stage number, NOT a green tick — fresh matters used to lie about progress.
   * current:        the stage the matter is currently in.
   * upcoming:       not yet reached.
   * skipped:        explicitly skipped (optional stage) or auto-skipped (predicate failed).
   */
  status: 'past' | 'past_unmarked' | 'current' | 'upcoming' | 'skipped';
  tasks: Array<{ name: string; description?: string; offset_days?: number; priority: string }>;
  deadlines: Array<{ name: string; offset_days: number; counts_court_days: boolean; severity?: string }>;
};
export type InterlocutoryChild = {
  ROWID: number | string;
  reference: string;
  matter_name: string;
  matter_type_id: number;
  interlocutory_type: string;
  status: 'open' | 'on_hold' | 'finalised' | 'archived';
  opened_at: string;
  finalised_at?: string;
  current_stage_id?: number;
  current_stage_entered_at?: string;
  relief_obtained?: string;
};
export type NewInterlocutoryInput = {
  interlocutory_type_code: string;
  reference?: string;
  matter_name?: string;
  court_id?: number | string;
  case_number?: string;
  relief_sought?: string;
  opened_at?: string;
};

export type MatterAccessLevel = 'none' | 'read' | 'comment' | 'write' | 'manage';
export type MatterAccessGrant = {
  ROWID: number | string; user_id: number; access_level: MatterAccessLevel;
  granted_at: string; granted_by: number;
};

// ---- Notifications ----
export const notificationApi = {
  list:        (unread_only = false) => http.get<{ notifications: Notification[]; unread: number }>(`/notifications${unread_only ? '?unread_only=true' : ''}`),
  unreadCount: () => http.get<{ unread: number }>('/notifications/unread-count'),
  markRead:    (id: number | string) => http.post(`/notifications/${id}/read`),
  markAllRead: () => http.post('/notifications/read-all'),
};
export type Notification = {
  ROWID: number | string;
  kind: string;
  title: string;
  body?: string | null;
  link?: string | null;
  severity?: string;
  read_at?: string | null;
  CREATEDTIME?: string;
};

// ---- AI ----
export const aiApi = {
  settings:     () => http.get<{ configured: boolean; settings?: { provider: string; model: string; enabled: boolean; api_key: string } }>('/ai/settings'),
  saveSettings: (body: { provider: string; model: string; api_key?: string; enabled?: boolean }) => http.put('/ai/settings', body),
  testSettings: (body: { provider: string; model: string; api_key?: string }) => http.post<{ ok: boolean }>('/ai/settings/test', body),
  models:       () => http.get<{ providers: string[]; models: Record<string, { value: string; label: string }[]> }>('/ai/models'),
  chat:         (body: { conversationId?: string; message: string }) =>
    http.post<{ conversationId: string; content: string; toolsUsed: { name: string; input: Record<string, unknown> }[]; usage?: { inputTokens: number; outputTokens: number } }>('/ai/chat', body),
  conversations: () => http.get<AiConversation[]>('/ai/conversations'),
  conversation:  (id: string) => http.get<AiMessage[]>(`/ai/conversations/${encodeURIComponent(id)}`),
  deleteConversation: (id: string) => http.del(`/ai/conversations/${encodeURIComponent(id)}`),
};
export type AiConversation = { id: string; preview: string; lastMessageAt: string };
export type AiMessage = {
  id: number; role: 'user'|'assistant'|'tool'; content: string;
  toolCalls?:   { id: string; name: string; input: Record<string, unknown> }[] | null;
  toolResults?: unknown | null;
  createdAt: string;
};

// ---- Rates ----
export const rateApi = {
  list:   () => http.get<{ rates: Rate[] }>('/rates'),
  create: (body: Partial<Rate>) => http.post<{ id: number }>('/rates', body),
  update: (id: number, patch: Partial<Rate>) => http.patch(`/rates/${id}`, patch),
  remove: (id: number) => http.del(`/rates/${id}`),
};
export type Rate = {
  ROWID: number; user_id?: number | null; name: string;
  activity_code?: string; unit: 'hour'|'day'|'fixed';
  rate_zar: number; effective_from: string; effective_to?: string | null;
  active: boolean | string;
};

// ---- Tasks ----
export const taskApi = {
  list: (params: { matter_id?: number | string; status?: string; assigned_to?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.matter_id != null) qs.set('matter_id', String(params.matter_id));
    if (params.status)            qs.set('status', params.status);
    if (params.assigned_to != null) qs.set('assigned_to', String(params.assigned_to));
    const s = qs.toString();
    return http.get<{ tasks: Task[] }>(`/tasks${s ? `?${s}` : ''}`);
  },
  create:   (body: NewTask) => http.post<{ id: number }>('/tasks', body),
  update:   (id: number | string, patch: Partial<Task>) => http.patch(`/tasks/${id}`, patch),
  remove:   (id: number | string) => http.del(`/tasks/${id}`),
  complete: (id: number | string) => http.post(`/tasks/${id}/complete`),
};
export type Task = {
  ROWID: number | string; matter_id: number | string; stage_id?: number | string | null;
  name: string; description?: string | null;
  status: 'open' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  due_date?: string | null; due_time?: string | null;
  assigned_to_user_id?: number | null;
  /** advocate | attorney | null. Drives Tasks-tab filter; attorney tasks visible read-only on opt-in. */
  owner_role?: string | null;
  billable: boolean | string;
  billing_status: string;
  completed_at?: string | null;
  // Joined server-side from the matters table for the Tasks dashboard.
  matter_name?: string | null;
  /** Server joins from matters.current_stage_id so the page can default to a current-stage filter. */
  matter_current_stage_id?: string | null;
  matter_reference?: string | null;
};
export type NewTask = {
  matter_id: number | string; name: string; description?: string;
  priority?: 'low'|'normal'|'high'|'urgent';
  due_date?: string; due_time?: string;
  assigned_to_user_id?: number; billable?: boolean; stage_id?: number | string;
};

// ---- Time entries ----
export const timeApi = {
  list: (params: { matter_id?: number | string; user_id?: number; from?: string; to?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.matter_id != null) qs.set('matter_id', String(params.matter_id));
    if (params.user_id != null)   qs.set('user_id',   String(params.user_id));
    if (params.from)              qs.set('from', params.from);
    if (params.to)                qs.set('to',   params.to);
    const s = qs.toString();
    return http.get<{ time_entries: TimeEntry[] }>(`/time-entries${s ? `?${s}` : ''}`);
  },
  create: (body: NewTimeEntry) => http.post<{ id: number }>('/time-entries', body),
  update: (id: number | string, patch: Partial<TimeEntry>) => http.patch(`/time-entries/${id}`, patch),
  remove: (id: number | string) => http.del(`/time-entries/${id}`),
};
export type TimeEntry = {
  ROWID: number | string; matter_id: number | string; task_id?: number | null; user_id?: number;
  rate_id?: number | null; work_date: string;
  start_time?: string | null; end_time?: string | null;
  duration_hours: number;
  activity_code?: string | null; description: string;
  billable: boolean | string;
  billing_status: string;
  invoice_id?: number | null;
};
export type NewTimeEntry = {
  matter_id: number | string; work_date?: string; duration_hours: number; description: string;
  task_id?: number | string; user_id?: number; rate_id?: number | string;
  start_time?: string; end_time?: string; activity_code?: string; billable?: boolean;
};

// ---- Diary ----
export const diaryApi = {
  list: (params: { from?: string; to?: string; matter_id?: number | string } = {}) => {
    const qs = new URLSearchParams();
    if (params.from)              qs.set('from', params.from);
    if (params.to)                qs.set('to',   params.to);
    if (params.matter_id != null) qs.set('matter_id', String(params.matter_id));
    const s = qs.toString();
    return http.get<{ events: DiaryEvent[] }>(`/diary${s ? `?${s}` : ''}`);
  },
  create: (body: NewDiaryEvent) => http.post<{ id: number }>('/diary', body),
  update: (id: number | string, patch: Partial<DiaryEvent>) => http.patch(`/diary/${id}`, patch),
  remove: (id: number | string) => http.del(`/diary/${id}`),
};
export type DiaryKind = 'court_date' | 'deadline' | 'consultation' | 'conference' | 'reminder' | 'personal';
export type DiaryEvent = {
  ROWID: number | string; matter_id?: number | string | null;
  kind: DiaryKind; title: string; description?: string | null;
  all_day: boolean | string;
  starts_at: string; ends_at?: string | null;
  timezone: string; location?: string | null;
  court_id?: number | null; severity: string; statutory_reference?: string | null;
  reminder_offsets_csv?: string | null;
};
export type NewDiaryEvent = {
  kind: DiaryKind; title: string; starts_at: string;
  description?: string; ends_at?: string; matter_id?: number | string;
  all_day?: boolean; timezone?: string; location?: string;
  court_id?: number | string; severity?: string; statutory_reference?: string;
  reminder_offsets_csv?: string;
};

// ---- Documents ----
export const documentApi = {
  list: (params: { matter_id?: number | string } = {}) => {
    const qs = new URLSearchParams();
    if (params.matter_id != null) qs.set('matter_id', String(params.matter_id));
    const s = qs.toString();
    return http.get<{ documents: DocumentMeta[] }>(`/documents${s ? `?${s}` : ''}`);
  },
  get: (id: number | string) => http.get<{ document: DocumentMeta; versions: DocumentMeta[] }>(`/documents/${id}`),
  requestUploadUrl: (body: { file_name: string; mime_type: string; size_bytes?: number }) =>
    http.post<{ object_id: string; object_key: string; upload_url: string; prefix: string; file_name: string; mime_type: string; expires_in_seconds: number }>('/documents/upload-url', body),
  create: (body: NewDocument) => http.post<{ id: number }>('/documents', body),
  update: (id: number | string, patch: Partial<DocumentMeta>) => http.patch(`/documents/${id}`, patch),
  remove: (id: number | string) => http.del(`/documents/${id}`),
  downloadUrl: (id: number | string) => http.get<{ download_url: string; file_name: string }>(`/documents/${id}/download-url`),
};

// ---- Files (generic two-step upload to Stratus) ----
// Used for everything that is not a primary "document" — branding logos,
// signer signatures, bank-CSV imports, ad-hoc attachments. Stored under
// `tenants/<tenantId>/<scope>/...` in the FILES_BUCKET_NAME bucket.
export type FileScope =
  | 'documents' | 'signing' | 'signatures'
  | 'branding' | 'bank-csv' | 'invoices' | 'ocr' | 'generic';

/** Metadata for a file row persisted in the generic `files` table. */
export type FileMeta = {
  id: string;
  name: string;
  content_type: string | null;
  byte_size: number;
  scope: string;
  parent_table: string | null;
  parent_row_id: string | null;
  uploaded_by: string | null;
  created_at: string | null;
  download_url: string;
};

/**
 * Upload a single file (multipart). Mirrors MyClinicOS's flow:
 *   - server streams into Stratus under tenants/<tenantId>/<scope>/
 *   - server persists a `files` row
 *   - returns the metadata for that row
 * Progress events fire as the browser uploads the bytes.
 */
export function uploadFile(
  file: File,
  opts: {
    scope: FileScope;
    parent_table?: string;
    parent_row_id?: string | number;
    onProgress?: (pct: number) => void;
  },
): Promise<{ file: FileMeta }> {
  const form = new FormData();
  form.append('file', file);
  form.append('scope', opts.scope);
  if (opts.parent_table)   form.append('parent_table', opts.parent_table);
  if (opts.parent_row_id != null) form.append('parent_row_id', String(opts.parent_row_id));
  return xhrUpload<{ file: FileMeta }>('/files', form, { onProgress: opts.onProgress });
}

export const fileApi = {
  list: (params: { scope?: FileScope; parent_table?: string; parent_row_id?: string | number } = {}) => {
    const qs = new URLSearchParams();
    if (params.scope) qs.set('scope', params.scope);
    if (params.parent_table) qs.set('parent_table', params.parent_table);
    if (params.parent_row_id != null) qs.set('parent_row_id', String(params.parent_row_id));
    const s = qs.toString();
    return http.get<{ files: FileMeta[] }>(`/files${s ? `?${s}` : ''}`);
  },
  /** Soft-deletes the row and best-effort removes the Stratus object. */
  remove: (id: string | number) =>
    http.del<{ ok: boolean; deleted: boolean }>(`/files/${encodeURIComponent(String(id))}`),
};

/** Stable per-file download URL — the server proxies the stream (no presigned
 *  URL leakage). Equivalent to MyClinicOS's fileDownloadUrl helper. */
export const fileDownloadUrl = (id: string | number) =>
  `/server/api/files/${encodeURIComponent(String(id))}/download`;

// ---- Tenant branding (logo + letterhead) ----
export const tenantBrandingApi = {
  uploadUrl: (body: { kind: 'logo' | 'letterhead'; file_name: string; mime_type: string; size_bytes?: number }) =>
    http.post<{ kind: string; object_id: string; object_key: string; upload_url: string; prefix: string; expires_in_seconds: number }>('/tenant/branding/upload-url', body),
  set: (body: { kind: 'logo' | 'letterhead'; object_id: string }) =>
    http.post<{ ok: boolean }>('/tenant/branding', body),
  downloadUrl: (kind: 'logo' | 'letterhead') =>
    http.get<{ download_url: string; kind: string; object_id: string }>(`/tenant/branding/${kind}/download-url`),
};

// Two-step PUT helper — works the same for every scope.
export async function uploadToStratus(uploadUrl: string, file: File): Promise<void> {
  const r = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!r.ok) throw new Error(`Upload failed: ${r.status} ${r.statusText}`);
}
export type DocumentMeta = {
  ROWID: number | string; matter_id?: number | string | null;
  document_type_id?: number | null;
  title: string; status: 'draft'|'in_review'|'final'|'filed'|'withdrawn'|'archived';
  confidentiality: 'public'|'internal'|'privileged'|'sealed';
  version: number; parent_document_id?: number | null;
  object_id: string; file_name: string; mime_type: string; size_bytes: number;
  sha256_hex?: string;
  ocr_status: string;
  court_online_filed: boolean | string; court_online_ref?: string;
  uploaded_by: number; notes?: string;
  CREATEDTIME?: string;
};
export type NewDocument = {
  object_id: string; file_name: string; mime_type: string; size_bytes: number;
  title: string; matter_id?: number | string;
  document_type_id?: number | string;
  status?: DocumentMeta['status']; confidentiality?: DocumentMeta['confidentiality'];
  sha256_hex?: string; notes?: string;
};

// ---- Disbursements ----
export const disbursementApi = {
  list:   (params: { matter_id?: number | string; billing_status?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.matter_id != null) qs.set('matter_id', String(params.matter_id));
    if (params.billing_status)    qs.set('billing_status', params.billing_status);
    const s = qs.toString();
    return http.get<{ disbursements: Disbursement[] }>(`/disbursements${s ? `?${s}` : ''}`);
  },
  create: (body: Partial<Disbursement> & { matter_id: number | string; incurred_on: string; description: string; amount_zar: number }) => http.post<{ id: number }>('/disbursements', body),
  update: (id: number | string, patch: Partial<Disbursement>) => http.patch(`/disbursements/${id}`, patch),
  remove: (id: number | string) => http.del(`/disbursements/${id}`),
};
export type Disbursement = {
  ROWID: number; matter_id: number; incurred_on: string; description: string;
  amount_zar: number; vat_zar?: number; vendor?: string; receipt_document_id?: number | null;
  billing_status: 'unbilled'|'on_proforma'|'billed'|'excluded';
  invoice_id?: number | null;
};

// ---- Invoices ----
// IDs widened to string|number — Catalyst ROWIDs are 17-19 digit BIGINTs that
// exceed JS safe-integer (2^53). Keep as digit strings end-to-end.
export const invoiceApi = {
  list: (params: { status?: string; kind?: string } = {}) => {
    const qs = new URLSearchParams(params as Record<string,string>).toString();
    return http.get<{ invoices: InvoiceSummary[] }>(`/invoices${qs ? `?${qs}` : ''}`);
  },
  get: (id: number | string) => http.get<{
    invoice: InvoiceDetail;
    lines: InvoiceLine[];
    payments: Payment[];
    bill_to: InvoiceBillToPart[] | null;
  }>(`/invoices/${id}`),
  proforma: (body: {
    matter_ids: (number | string)[];
    billed_attorney_id?: number | string;
    billed_firm_id?: number | string;
    /** Optional explicit selections — when present, only these rows go on the proforma.
     *  Omit both to include every unbilled item across the matter_ids. */
    time_entry_ids?: (number | string)[];
    disbursement_ids?: (number | string)[];
  }) =>
    http.post<{ id: number | string; number: string }>('/invoices/proforma', body),
  update: (id: number | string, patch: Partial<InvoiceDetail>) => http.patch(`/invoices/${id}`, patch),
  issue:  (id: number | string) => http.post<{ ok: boolean; number: string }>(`/invoices/${id}/issue`),
  void:   (id: number | string) => http.post<{ ok: boolean; time_released?: number; disb_released?: number; already_void?: boolean }>(`/invoices/${id}/void`),
  email:  (id: number | string, body: { to?: string; message?: string } = {}) =>
    http.post<{ ok: boolean; message_id: string }>(`/invoices/${id}/email`, body),
  pdfUrl: (id: number | string) => `/server/api/invoices/${id}/pdf`,

  // Line CRUD — only allowed while invoice.status === 'draft'.
  addLine:    (id: number | string, body: NewInvoiceLine) =>
    http.post<{ id: string; subtotal_zar: number; tax_zar: number; total_zar: number; invoice_totals: InvoiceTotals }>(`/invoices/${id}/lines`, body),
  patchLine:  (id: number | string, lineId: number | string, patch: Partial<NewInvoiceLine>) =>
    http.patch<{ ok: boolean; subtotal_zar: number; tax_zar: number; total_zar: number; invoice_totals: InvoiceTotals }>(`/invoices/${id}/lines/${lineId}`, patch),
  deleteLine: (id: number | string, lineId: number | string) =>
    http.del<{ ok: boolean; invoice_totals: InvoiceTotals }>(`/invoices/${id}/lines/${lineId}`),

  // Recall an issued invoice back to draft (no payments allowed).
  recall: (id: number | string) =>
    http.post<{ ok: boolean; fee_book_rolled_back: number }>(`/invoices/${id}/recall`),

  // Issue a credit note. Body lines optional — default credits the whole invoice.
  creditNote: (id: number | string, body: { reason?: string; lines?: { line_id: number | string; quantity?: number }[] } = {}) =>
    http.post<{
      id: string; number: string;
      against_invoice_id: string; against_invoice_number: string;
      credited_subtotal_zar: number; credited_total_zar: number;
      original_status_now: string;
    }>(`/invoices/${id}/credit-note`, body),
};
export type InvoiceSummary = {
  ROWID: number | string; number: string; kind: string; status: string;
  issue_date?: string; due_date?: string;
  billed_attorney_id?: number | string; billed_firm_id?: number | string;
  currency: string; subtotal_zar: number; vat_zar: number; total_zar: number; amount_paid_zar: number;
};
export type InvoiceDetail = InvoiceSummary & {
  notes?: string;
  issued_by?: number | string; issued_at?: string;
  bill_to_json?: string;
};
export type InvoiceLine = {
  ROWID: number | string; invoice_id: number | string; matter_id?: number | string;
  kind: 'time_entry' | 'disbursement' | 'fixed_fee' | 'discount' | 'adjustment';
  time_entry_id?: number | string; disbursement_id?: number | string;
  description: string;
  /** Slot detail for time-entry lines — drives "08:31–09:02 · drafting" rendering. */
  work_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  activity_code?: string | null;
  quantity: number; unit: string; rate_zar: number;
  tax_rate_code?: string; tax_percent: number;
  subtotal_zar: number; tax_zar: number; total_zar: number; display_order: number;
};

/** Render "08:31–09:02 · drafting" (or as much of it as is available)
 *  alongside an invoice-line description. Returns null when there's nothing
 *  to add (non-time line, or legacy entry without start/end). */
export function timeSlotLabel(line: Partial<InvoiceLine>): string | null {
  if (line.kind && line.kind !== 'time_entry') return null;
  const parts: string[] = [];
  if (line.work_date) parts.push(line.work_date);
  if (line.start_time && line.end_time) parts.push(`${line.start_time}–${line.end_time}`);
  else if (line.start_time)             parts.push(`from ${line.start_time}`);
  if (line.activity_code) parts.push(line.activity_code);
  return parts.length ? parts.join(' · ') : null;
}
export type NewInvoiceLine = {
  description: string;
  quantity: number;
  rate_zar: number;
  kind?: InvoiceLine['kind'];
  unit?: string;
  tax_percent?: number;
  tax_rate_code?: string;
  matter_id?: number | string | null;
  display_order?: number;
};
export type InvoiceTotals = { subtotal_zar: number; vat_zar: number; total_zar: number };
export type InvoiceBillToPart =
  | { kind: 'attorney'; id: number | string; name: string; email?: string }
  | { kind: 'firm';     id: number | string; name: string; email?: string; vat_number?: string };

// ---- Payments ----
// IDs widened to string|number — Catalyst ROWIDs exceed JS safe-integer.
export const paymentApi = {
  list: (invoice_id?: number | string) =>
    http.get<{ payments: Payment[] }>(`/payments${invoice_id ? `?invoice_id=${invoice_id}` : ''}`),
  create: (body: Partial<Payment>) => http.post<{ id: number | string; invoice_status: string; amount_paid_zar: number }>('/payments', body),
  remove: (id: number | string) => http.del(`/payments/${id}`),
};
export type Payment = {
  ROWID: number | string;
  invoice_id: number | string;
  received_on: string;
  amount_zar: number;
  method: 'eft'|'card'|'cash'|'trust_transfer'|'other';
  reference?: string; gateway?: string; gateway_ref?: string; notes?: string;
};

// ---- Fee book ----
export const feeBookApi = {
  list: (params: { from?: string; to?: string; counsel?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.from)    qs.set('from', params.from);
    if (params.to)      qs.set('to',   params.to);
    if (params.counsel) qs.set('counsel', String(params.counsel));
    const s = qs.toString();
    return http.get<{
      entries: FeeBookEntry[];
      totals: { entries: number; hours: number; amount_zar: number };
      filters: { from: string | null; to: string | null; counsel: number | null };
    }>(`/fee-book${s ? `?${s}` : ''}`);
  },
};
export type FeeBookEntry = {
  ROWID: number; matter_id: number; entry_date: string; description: string;
  counsel_user_id: number; hours?: number; amount_zar: number;
};

// ---- Precedents (Phase 3) ----
export const precedentApi = {
  list:   (params: { q?: string; category?: string } = {}) => {
    const qs = new URLSearchParams(params as Record<string,string>).toString();
    return http.get<{ precedents: Precedent[] }>(`/precedents${qs ? `?${qs}` : ''}`);
  },
  get:    (id: number) => http.get<{ precedent: Precedent }>(`/precedents/${id}`),
  create: (body: Partial<Precedent>) => http.post<{ id: number }>('/precedents', body),
  update: (id: number, patch: Partial<Precedent>) => http.patch(`/precedents/${id}`, patch),
  remove: (id: number) => http.del(`/precedents/${id}`),
};
export type Precedent = {
  ROWID: number; title: string; category: string;
  summary?: string; body?: string; tags?: string;
  source_document_id?: number | null; created_by?: number;
  CREATEDTIME?: string; MODIFIEDTIME?: string;
};

// ---- Trust accounting (Phase 4) ----
export const trustApi = {
  list:     (matter_id?: number) =>
    http.get<{ transactions: TrustTransaction[]; balance_zar: number | null }>(`/trust${matter_id ? `?matter_id=${matter_id}` : ''}`),
  balances: () => http.get<{ balances: { matter_id: number; balance_zar: number }[]; total_zar: number }>('/trust/balances'),
  record:   (body: Partial<TrustTransaction>) => http.post<{ id: number; balance_zar: number }>('/trust', body),
  remove:   (id: number) => http.del(`/trust/${id}`),
};
export type TrustTransaction = {
  ROWID: number; matter_id: number; txn_date: string;
  kind: 'deposit'|'fees_drawn'|'disbursement_paid'|'refund_to_client'|'transfer_out'|'adjustment';
  amount_zar: number; counterparty?: string; reference?: string; notes?: string;
  recorded_by?: number; CREATEDTIME?: string;
};

// ---- Bill of costs (Phase 4) ----
export const billOfCostsApi = {
  export: (matterId: number | string, scale: '67A' | '70' = '70') =>
    http.get<BillOfCosts>(`/bill-of-costs/${matterId}?scale=${scale}`),
  // The PDF route streams a binary; we surface a plain URL so the SPA can
  // window.open() it and let the browser handle the auth headers via fetch +
  // download (same approach as invoiceApi.pdfUrl).
  pdfUrl: (matterId: number | string, scale: '67A' | '70' = '70') =>
    `/server/api/bill-of-costs/${matterId}/pdf?scale=${scale}`,
};
export type BillOfCostsItem = {
  item_no: number; date: string; description: string;
  units?: number; unit?: string; rate_zar?: number;
  amount_zar: number; vat_zar?: number; total_zar?: number;
  activity_code?: string | null; scale_ref?: string | null; vendor?: string | null;
};
export type BillOfCosts = {
  scale: '67A' | '70';
  matter: { id: number; reference: string; name: string; case_number?: string; opened_at: string };
  court: { name: string; tier: string } | null;
  parties: { role: string; side: string; name: string }[];
  schedules: {
    A: { label: string; items: BillOfCostsItem[] };
    B: { label: string; items: BillOfCostsItem[] };
    C: { label: string; items: BillOfCostsItem[] };
  };
  subtotals: { schedule_a_zar: number; schedule_b_zar: number; schedule_c_zar: number; grand_total_zar: number };
  notes: string[];
};

// ---- Signing (Phase 5) ----
export const signingApi = {
  list:         (params: { matter_id?: number; status?: string } = {}) => {
    const qs = new URLSearchParams(params as Record<string,string>).toString();
    return http.get<{ envelopes: SigningEnvelope[] }>(`/signing/envelopes${qs ? `?${qs}` : ''}`);
  },
  get:          (id: number) => http.get<{ envelope: SigningEnvelope; signers: Signer[]; fields: SignatureField[] }>(`/signing/envelopes/${id}`),
  create:       (body: { title: string; source_document_id: number; matter_id?: number; signing_order?: 'serial'|'parallel'; message?: string }) =>
    http.post<{ id: number }>('/signing/envelopes', body),
  addSigner:    (id: number, body: { name: string; email: string; role?: 'signer'|'approver'|'cc'; require_otp?: boolean }) =>
    http.post<{ id: number }>(`/signing/envelopes/${id}/signers`, body),
  addField:     (id: number, body: Partial<SignatureField> & { signer_id: number }) =>
    http.post<{ id: number }>(`/signing/envelopes/${id}/fields`, body),
  send:         (id: number) => http.post<{ ok: boolean; sent: { signer_id: number; email: string; url: string }[] }>(`/signing/envelopes/${id}/send`),
  voidIt:       (id: number) => http.post(`/signing/envelopes/${id}/void`),
  audit:        (id: number) => http.get<{ events: { event_type: string; occurred_at: string; payload_json?: string }[] }>(`/signing/envelopes/${id}/audit`),
};
export type SigningEnvelope = {
  ROWID: number; matter_id?: number; title: string; status: string;
  source_document_id: number; final_document_id?: number;
  signing_order: 'serial'|'parallel'; message?: string;
  sent_at?: string; completed_at?: string; expires_at?: string;
  final_sha256_hex?: string;
};
export type Signer = {
  ROWID: number; display_order: number; name: string; email: string;
  role: 'signer'|'approver'|'cc'; status: string; require_otp: boolean | string;
  signed_at?: string; signer_ip?: string;
};
export type SignatureField = {
  ROWID: number; signer_id: number; page: number;
  x: number; y: number; w: number; h: number;
  field_type: 'signature'|'initial'|'date'|'text'|'checkbox';
  required: boolean | string; value?: string;
};

// ---- Integrations ----
export const integrationApi = {
  list:     () => http.get<{ integrations: IntegrationRow[] }>('/integrations'),
  saveSmtp: (body: SmtpConfig) => http.put('/integrations/smtp', body),
  testSmtp: (to?: string) => http.post<{ ok: boolean }>('/integrations/smtp/test', to ? { to } : undefined),
};

// ---- Platform billing — tenant-facing (Phase 6) ----
// The tenant's view of their Litigate subscription: plan, usage, issued
// platform invoices, change-plan / cancel / resume. Payment flow rides on
// the existing invoice → PayFast link / manual EFT path; we don't embed a
// gateway here.
export type TenantSubscription = {
  ROWID: TenantId;
  plan_code: string;
  status: 'trial' | 'active' | 'past_due' | 'cancelled';
  billing_cycle: 'monthly' | 'annual';
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean | string;
  cancelled_at?: string;
  started_at: string;
  pending_plan_code?: string;
};
export type PublicPlan = {
  code: string; name: string; description?: string; tier_rank: number;
  billing_cycle: 'monthly' | 'annual';
  price_monthly_zar: number; price_annual_zar: number;
  trial_days: number; currency: string;
};
export type PlatformInvoice = {
  ROWID: TenantId; number: string; status: string;
  issued_at: string; due_at?: string;
  period_start?: string; period_end?: string;
  subtotal_zar: number; vat_zar: number; total_zar: number; amount_paid_zar: number;
};
export type UsageSnapshot = {
  metric_key: string;
  current_value: number;
  peak_value: number;
  period_start: string;
  period_end: string;
};
export const platformBillingApi = {
  subscription: () => http.get<{
    subscription: TenantSubscription | null;
    plan: PublicPlan | null;
    entitlements: { feature_key: string; enabled: string | boolean; source: string }[];
    limits: { metric_key: string; limit_value: number; soft_limit_value: number; source: string }[];
    usage: UsageSnapshot[];
  }>('/platform-billing/subscription'),
  publicPlans: () => http.get<{ plans: PublicPlan[] }>('/platform-billing/plans-public'),
  changePlan:  (plan_code: string) => http.post<{ ok: boolean; effective_from?: string; no_change?: boolean }>('/platform-billing/change-plan', { plan_code }),
  cancel:      () => http.post('/platform-billing/cancel'),
  resume:      () => http.post('/platform-billing/resume'),
  invoices:    () => http.get<{ invoices: PlatformInvoice[] }>('/platform-billing/invoices'),
  invoice:     (id: TenantId) => http.get<{ invoice: PlatformInvoice; lines: { ROWID: TenantId; kind: string; description: string; quantity: number; unit_price_zar: number; line_total_zar: number; plan_code?: string; addon_code?: string }[] }>(`/platform-billing/invoices/${id}`),
};

// ---- Calendar sync (Phase 7) ----
type ProviderStatus = {
  configured: boolean;
  connected: boolean;
  calendar_id?: string;
  last_sync_at?: string | null;
  last_sync_status?: string | null;
  last_sync_error?: string | null;
};
export type CalendarStatus = {
  google_calendar: ProviderStatus;
  microsoft_calendar: ProviderStatus;
  caldav: ProviderStatus & { calendar_url?: string; username?: string };
};
export const calendarApi = {
  status:    () => http.get<CalendarStatus>('/calendars/status'),
  googleAuthUrl: () => http.post<{ url: string }>('/calendars/google/auth-url'),
  googleDisconnect: () => http.post('/calendars/google/disconnect'),
  googleSyncNow: () => http.post<{ pulled?: number; skipped?: boolean; token_expired?: boolean }>('/calendars/google/sync-now'),
  microsoftAuthUrl: () => http.post<{ url: string }>('/calendars/microsoft/auth-url'),
  microsoftDisconnect: () => http.post('/calendars/microsoft/disconnect'),
  microsoftSyncNow: () => http.post<{ pulled?: number; skipped?: boolean; token_expired?: boolean }>('/calendars/microsoft/sync-now'),
  caldavConnect: (body: { calendar_url: string; username: string; app_password: string }) =>
    http.post<{ ok: boolean }>('/calendars/caldav/connect', body),
  caldavDisconnect: () => http.post('/calendars/caldav/disconnect'),
  caldavTest: () => http.post<{ ok: boolean; status?: number; error?: string }>('/calendars/caldav/test'),
};

// ---- POPIA data subject requests (Phase 6) ----
export type DsrKind = 'access' | 'correction' | 'deletion' | 'objection';
export type DsrStatus = 'received' | 'acknowledged' | 'in_progress' | 'resolved' | 'refused';
export type DsrRequest = {
  ROWID: TenantId;
  reference_code: string;
  kind: DsrKind;
  subject_name: string;
  subject_email: string;
  subject_id_number?: string;
  related_party_id?: number | null;
  details?: string | null;
  status: DsrStatus;
  response_notes?: string | null;
  submitted_at: string;
  resolved_at?: string | null;
  handled_by?: number | null;
};
export const popiaApi = {
  list: (status?: DsrStatus) =>
    http.get<{ requests: DsrRequest[] }>(`/popia/requests${status ? `?status=${status}` : ''}`),
  update: (id: TenantId, patch: { status?: DsrStatus; response_notes?: string }) =>
    http.patch(`/popia/requests/${id}`, patch),
  dataPack: (id: TenantId) =>
    http.get<{ tenant_id: TenantId; generated_at: string; request_id: TenantId; holdings: Record<string, unknown[]> }>(
      `/popia/requests/${id}/data-pack`,
    ),
};

// ---- Attorney portal access (Phase 5) ----
// Tokenised read-only matter snapshot for the instructing attorney. Granted
// per-matter; the raw token is only returned on creation and never stored.
export type AttorneyPortalGrant = {
  ROWID: TenantId;
  attorney_id: number;
  status: 'active' | 'revoked' | 'expired';
  expires_at: string | null;
  last_used_at: string | null;
  granted_at: string;
  revoked_at: string | null;
  attorney: { ROWID: number; first_name?: string; last_name?: string; email?: string } | null;
};
export const attorneyPortalApi = {
  list: (matterId: number | string) =>
    http.get<{ access: AttorneyPortalGrant[] }>(`/matters/${matterId}/portal-access`),
  grant: (matterId: number | string, body: { attorney_id: number; ttl_days?: number }) =>
    http.post<{ id: number; url: string; attorney: { id: number; name: string; email?: string }; expires_at: string }>(
      `/matters/${matterId}/portal-access`, body,
    ),
  revoke: (matterId: number | string, grantId: TenantId) =>
    http.del(`/matters/${matterId}/portal-access/${grantId}`),
};

// ---- Bank CSV import (Phase 4) ----
// Reconciliation-only path. Direct bank-feed APIs are explicitly out of scope
// (policy decision, not coming back). Client parses the CSV; server stores
// the normalised rows and links each one to a payment or trust deposit.
export type BankCsvImport = {
  ROWID: TenantId; account_label: string; row_count: number; matched_count: number;
  status: 'uploaded' | 'reconciled_partial' | 'reconciled';
  uploaded_by: number; uploaded_at: string; CREATEDTIME?: string;
};
export type BankCsvRow = {
  ROWID: TenantId; row_num: number;
  transaction_date: string | null;
  description: string | null;
  amount_zar: number;
  reference: string | null;
  matched_kind: 'payment' | 'trust_deposit' | null;
  matched_id: TenantId | null;
  matched_at: string | null;
};
export const bankCsvApi = {
  listImports: () => http.get<{ imports: BankCsvImport[] }>('/bank-csv/imports'),
  createImport: (body: {
    account_label: string;
    mapping: Record<string, string>;
    rows: Array<{ row_num: number; transaction_date?: string; description?: string; amount_zar: number; reference?: string }>;
  }) => http.post<{ id: string; row_count: number }>('/bank-csv/imports', body),
  listRows: (importId: TenantId) =>
    http.get<{ rows: BankCsvRow[] }>(`/bank-csv/imports/${importId}/rows`),
  matchRow: (rowId: TenantId, body:
    | { kind: 'payment'; target: { invoice_id: TenantId } }
    | { kind: 'trust_deposit'; target: { matter_id: TenantId } }
  ) => http.post<{ ok: boolean; kind: string; matched_id: string }>(`/bank-csv/rows/${rowId}/match`, body),
  unmatchRow: (rowId: TenantId) => http.post(`/bank-csv/rows/${rowId}/unmatch`),
};

// ---- PayFast ----
export const payfastApi = {
  // Backend returns 412 with code `payfast_not_configured` when the tenant
  // has no creds or the invoice has no outstanding balance — surface that to
  // the user as a configuration prompt rather than a generic failure.
  generateLink: (invoice_id: number | string) =>
    http.post<{ url: string; amount_zar: number; invoice_number: string }>('/payfast/link', { invoice_id }),
  saveCredentials: (body: { merchant_id: string; merchant_key: string; passphrase?: string; mode?: 'live'|'sandbox'; enabled?: boolean }) =>
    http.put('/payfast/credentials', body),
  removeCredentials: () => http.del('/payfast/credentials'),
};

// ---- Platform admin ----
export type PlatformPlan = {
  ROWID: TenantId;
  code: string; name: string; description?: string;
  tier_rank: number;
  billing_cycle: 'monthly' | 'annual';
  price_monthly_zar: number; price_annual_zar: number;
  trial_days: number;
  currency: string;
  is_active: boolean | string;
  is_public: boolean | string;
};
export type PlatformAddon = {
  ROWID: TenantId;
  code: string; name: string; description?: string;
  price_monthly_zar: number; price_annual_zar: number;
  grants_feature_key?: string; grants_metric_key?: string; grants_metric_delta?: number;
  is_active: boolean | string;
};
export const platformApi = {
  impersonate: (tenant_id: TenantId) =>
    http.post<{ token: string; expires_at: string; tenant: { id: TenantId; name: string; status: string } }>(`/platform/tenants/${tenant_id}/impersonate`),
  seedCourtCalendar: () =>
    http.post<{ ok: boolean; inserted: number; already_present: number; total?: number; note?: string }>('/platform/seed/court-calendar'),
  // Dedupe procedural_stages + party_role_templates after re-seed-induced
  // triplication. Repoints any matter.current_stage_id pointing at a deleted
  // duplicate to the surviving row. Idempotent — safe to re-run.
  cleanupDuplicateCatalog: () =>
    http.post<{ ok: boolean; stages: { dropped: number; kept: number }; party_roles: { dropped: number; kept: number }; orphan_templates: number; matters_repointed: number }>('/platform/cleanup/duplicate-catalog'),
  // Idempotently inserts any matter types from the canonical seed that are
  // missing from the live matter_types table. Purely additive — existing rows
  // are not touched and not updated, so children pointing at them stay valid.
  // Run this FIRST when adding new matter types (e.g. the 5 specialty types
  // from Erasmus D6/D7/D8/D9: pie_eviction, interdict_final,
  // interdict_interlocutory, spoliation, anton_piller). Children get seeded
  // afterwards via seedProceduralStages / seedPartyRoleTemplates.
  seedMatterTypes: () =>
    http.post<{ ok: boolean; inserted: number; already_present: number; reactivated?: number; total: number; inserted_codes: string[]; reactivated_codes?: string[] }>('/platform/seed/matter-types'),
  // Re-insert procedural_stages against the real matter_types ROWIDs. Same
  // BIGINT-precision failure mode as seedPartyRoleTemplates — stage rows ended
  // up pointing at matter_type_ids that no longer exist after a matter_types
  // re-seed, so the stage playbook query returned empty. Nukes ZA stages and
  // re-inserts fresh. Run AFTER seedMatterTypes (so any new matter types have
  // ROWIDs to point at) and BEFORE seedStageTemplates (which depends on stages
  // existing). Idempotent.
  seedProceduralStages: () =>
    http.post<{ ok: boolean; deleted_existing: number; inserted: number; skipped_no_matter_type: number; matter_types_in_db: number }>('/platform/seed/procedural-stages'),
  // Re-populate stage_task_templates + stage_deadline_templates after the
  // BIGINT-precision bug in the seeder destroyed the stage_id FKs. Run after
  // seedProceduralStages and before cleanupDuplicateCatalog. Idempotent.
  seedStageTemplates: () =>
    http.post<{ ok: boolean; tasks_inserted: number; deadlines_inserted: number; skipped_stages: number; stages_in_db: number; matter_types_in_db: number }>('/platform/seed/stage-templates'),
  // Re-insert party_role_templates against the real matter_types ROWIDs.
  // The original seeder lost precision on the 17-19 digit BIGINT FK and
  // left every role template pointing at a non-existent matter_type, which
  // is why PartiesPanel had nowhere to add parties. Nukes and re-inserts
  // — idempotent.
  seedPartyRoleTemplates: () =>
    http.post<{ ok: boolean; deleted_existing: number; inserted: number; skipped_no_matter_type: number; matter_types_in_db: number }>('/platform/seed/party-role-templates'),
  // Idempotently seeds the 43-type interlocutory catalogue (master §10.4) plus
  // each type's stage flow + type-level deadlines. Existing rows skipped — safe
  // to re-run. PRECONDITION: interlocutory_types, interlocutory_stage_templates,
  // and interlocutory_deadline_templates tables must exist in the Catalyst
  // Console (DEPLOY.md §3 has the column specs).
  seedInterlocutoryTypes: () =>
    http.post<{ ok: boolean; types_inserted: number; stages_inserted: number; deadlines_inserted: number; types_in_seed: number; types_in_db: number }>('/platform/seed/interlocutory-types'),
  // Idempotently inserts SA lower courts (Magistrate / Branch / Detached / Periodical
  // — 509 operational rows from the OCJ register) into the existing `courts` table.
  // Skips any code already present, so re-runs are safe and high-court / regional /
  // specialised entries already there are never touched.
  seedLowerCourts: () =>
    http.post<{ ok: boolean; placeholders_deleted: number; inserted: number; updated: number; unchanged: number; total: number; sample_inserted_codes: string[]; sample_updated_codes: string[] }>('/platform/seed/lower-courts'),
  // Higher courts: Constitutional / SCA / High Court divisions / specialised /
  // tribunals / Small Claims (national catch-all). ~30 rows. Reactivates any
  // existing rows with active=NULL so the picker can see them.
  seedHigherCourts: () =>
    http.post<{ ok: boolean; inserted: number; reactivated: number; unchanged: number; total: number; sample_inserted_codes: string[]; sample_reactivated_codes: string[] }>('/platform/seed/higher-courts'),
  // One-shot recovery for time_entries / tasks orphaned by the legacy
  // BIGINT-rounding bug. For each row whose matter_id doesn't match a
  // real matter, finds the matter R where Number(R) === stored value and
  // rewrites the FK back to the un-rounded R. Idempotent.
  repairOrphanRowids: () =>
    http.post<{
      ok: boolean;
      time_entries: { scanned: number; repaired: number; unrecoverable: number; sample: { row_id: string; from: string; to: string }[] };
      tasks:        { scanned: number; repaired: number; unrecoverable: number; sample: { row_id: string; from: string; to: string }[] };
    }>('/platform/repair/orphan-rowids'),

  // Plans
  listPlans: () => http.get<{ plans: PlatformPlan[] }>('/platform/plans'),
  createPlan: (body: Partial<PlatformPlan>) => http.post<{ id: string }>('/platform/plans', body),
  updatePlan: (id: TenantId, patch: Partial<PlatformPlan>) => http.patch(`/platform/plans/${id}`, patch),
  deletePlan: (id: TenantId) => http.del(`/platform/plans/${id}`),

  // Addons
  listAddons: () => http.get<{ addons: PlatformAddon[] }>('/platform/addons'),
  createAddon: (body: Partial<PlatformAddon>) => http.post<{ id: string }>('/platform/addons', body),
  updateAddon: (id: TenantId, patch: Partial<PlatformAddon>) => http.patch(`/platform/addons/${id}`, patch),
  deleteAddon: (id: TenantId) => http.del(`/platform/addons/${id}`),
};
export type IntegrationRow = { kind: string; profile: string; enabled: boolean; last_ok_at?: string; last_error_message?: string };
export type SmtpConfig = { host: string; port: number; secure?: boolean; username?: string; password?: string; from_address: string; from_name?: string };
